/*
 * MSCVTR — eigen componenten (Horizon-migratie, pakket 2, 20-10-2026)
 *
 * Nieuw geschreven, zonder afhankelijkheden. Vervangt de web-componenten van het oude thema:
 *   <mscvtr-tabs>       tabs met knoppen (role=tab) en panelen (role=tabpanel), geen shadow DOM
 *   <mscvtr-carrousel>  horizontale rij met scroll-snap, vorige/volgende-knoppen en (optioneel) stippen
 *   <mscvtr-lade>       zijpaneel op een <dialog>; open via elke knop met aria-controls="<id>"
 * plus een vertaallaag: Horizon-events → de oude namen die de eigen code gebruikt
 *   variant:change  (uit Shopify's ProductSelectEvent)
 *   cart:change     (uit Shopify's CartLinesUpdateEvent)
 *   cart:refresh    (eigen code vraagt om verversen → Horizon's winkelwagen wordt opnieuw opgehaald)
 *
 * Openbare hulpjes op window.MSCVTR:
 *   verversWinkelwagen({ open })  haalt /cart.js op en laat Horizon lade, teller en cartpagina bijwerken
 *   scrollContainer()             het element dat scrolt (desktop ≥ 990 px: .page-wrapper, anders het document)
 */
(() => {
  if (window.MSCVTR && window.MSCVTR.componenten) return;
  const MSCVTR = (window.MSCVTR = window.MSCVTR || {});
  MSCVTR.componenten = true;

  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  let teller = 0;
  const zorgVoorId = (el, voorvoegsel) => {
    if (!el.id) el.id = `${voorvoegsel}-${++teller}`;
    return el.id;
  };

  /* ── Scrollcontainer (Horizon laat op desktop .page-wrapper scrollen) ─────────────── */
  MSCVTR.scrollContainer = () => {
    const wrapper = document.querySelector('.page-wrapper');
    if (wrapper && window.matchMedia('(min-width: 990px)').matches && wrapper.scrollHeight > wrapper.clientHeight) {
      return wrapper;
    }
    return document.scrollingElement || document.documentElement;
  };

  /* ── Tabs ───────────────────────────────────────────────────────────────────────── */
  class MscvtrTabs extends HTMLElement {
    connectedCallback() {
      if (this._klaar) return;
      this._klaar = true;
      this.tabs = [...this.querySelectorAll(':scope [role="tab"]')].filter((t) => t.closest('mscvtr-tabs') === this);
      this.panelen = [...this.querySelectorAll(':scope [role="tabpanel"]')].filter((p) => p.closest('mscvtr-tabs') === this);
      const lijst = this.querySelector('[role="tablist"]');
      if (lijst && !lijst.hasAttribute('aria-orientation')) lijst.setAttribute('aria-orientation', 'horizontal');

      this.tabs.forEach((tab, i) => {
        const paneel = this.panelen[i];
        if (!paneel) return;
        const tabId = zorgVoorId(tab, 'mscvtr-tab');
        const paneelId = zorgVoorId(paneel, 'mscvtr-tabpaneel');
        tab.setAttribute('aria-controls', paneelId);
        paneel.setAttribute('aria-labelledby', tabId);
        if (!paneel.hasAttribute('tabindex')) paneel.setAttribute('tabindex', '0');
        tab.addEventListener('click', () => this.kies(i));
        tab.addEventListener('keydown', (e) => this._toets(e, i));
      });

      let start = this.tabs.findIndex((t) => t.getAttribute('aria-selected') === 'true');
      if (start < 0) start = Math.max(0, this.panelen.findIndex((p) => !p.hidden));
      this.kies(start, { stil: true });
    }

    get gekozen() {
      return this.tabs.findIndex((t) => t.getAttribute('aria-selected') === 'true');
    }

    kies(index, { focus = false, stil = false } = {}) {
      if (index < 0 || index >= this.tabs.length) return;
      const vorige = this.gekozen;
      this.tabs.forEach((tab, i) => {
        const aan = i === index;
        tab.setAttribute('aria-selected', aan ? 'true' : 'false');
        tab.setAttribute('tabindex', aan ? '0' : '-1');
        if (this.panelen[i]) this.panelen[i].hidden = !aan;
      });
      if (focus) this.tabs[index].focus();
      if (!stil && vorige !== index) {
        this.dispatchEvent(new CustomEvent('mscvtr:tab-wissel', { bubbles: true, detail: { index, tab: this.tabs[index], paneel: this.panelen[index] } }));
      }
      // Carrousels in een paneel dat net zichtbaar werd: knoppen bijwerken.
      this.panelen[index]?.querySelectorAll('mscvtr-carrousel').forEach((c) => c.werkBij && c.werkBij());
    }

    _toets(e, i) {
      const n = this.tabs.length;
      let doel = null;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') doel = (i + 1) % n;
      else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') doel = (i - 1 + n) % n;
      else if (e.key === 'Home') doel = 0;
      else if (e.key === 'End') doel = n - 1;
      if (doel === null) return;
      e.preventDefault();
      this.kies(doel, { focus: true });
    }
  }

  /* ── Carrousel ──────────────────────────────────────────────────────────────────── */
  /*
   * Opbouw:
   * <mscvtr-carrousel>
   *   <button data-mscvtr-vorige>…</button>
   *   <div class="mscvtr-carrousel__spoor"> …kaarten… </div>
   *   <button data-mscvtr-volgende>…</button>
   *   <div class="mscvtr-carrousel__stippen" data-mscvtr-stippen></div>   (optioneel)
   * </mscvtr-carrousel>
   * Knoppen mogen ook buiten het element staan: aria-controls="<id van de carrousel>".
   */
  class MscvtrCarrousel extends HTMLElement {
    connectedCallback() {
      if (this._klaar) return;
      this._klaar = true;
      this.spoor = this.querySelector('.mscvtr-carrousel__spoor') || this.firstElementChild;
      if (!this.spoor) return;
      zorgVoorId(this, 'mscvtr-carrousel');
      this.werkBij = this.werkBij.bind(this);

      this.spoor.addEventListener('scroll', () => {
        if (this._raf) return;
        this._raf = requestAnimationFrame(() => { this._raf = null; this.werkBij(); });
      }, { passive: true });
      if ('ResizeObserver' in window) new ResizeObserver(this.werkBij).observe(this.spoor);
      this._sleep();
      this._stippen();
      this.werkBij();
    }

    get knoppen() {
      const eigen = [...this.querySelectorAll('[data-mscvtr-vorige], [data-mscvtr-volgende]')];
      const extern = [...document.querySelectorAll(`[aria-controls="${this.id}"]:is([data-mscvtr-vorige], [data-mscvtr-volgende])`)];
      return [...new Set([...eigen, ...extern])];
    }

    get stap() {
      // Eén "pagina": de zichtbare breedte, afgerond op hele kaarten.
      const kaart = this.spoor.firstElementChild;
      const breedte = this.spoor.clientWidth;
      if (!kaart) return breedte;
      const stijl = getComputedStyle(this.spoor);
      const tussen = parseFloat(stijl.columnGap || stijl.gap) || 0;
      const kaartBreedte = kaart.getBoundingClientRect().width + tussen;
      if (!kaartBreedte) return breedte;
      return Math.max(1, Math.floor((breedte + tussen) / kaartBreedte)) * kaartBreedte;
    }

    vorige() { this._ga(-1); }
    volgende() { this._ga(1); }

    _ga(richting) {
      const rtl = getComputedStyle(this.spoor).direction === 'rtl' ? -1 : 1;
      this.spoor.scrollBy({ left: richting * rtl * this.stap, behavior: reduceMotion() ? 'auto' : 'smooth' });
    }

    werkBij() {
      if (!this.spoor) return;
      const max = this.spoor.scrollWidth - this.spoor.clientWidth;
      const pos = Math.abs(this.spoor.scrollLeft);
      const kanScrollen = max > 2;
      this.toggleAttribute('data-scrollbaar', kanScrollen);
      this.knoppen.forEach((knop) => {
        const isVorige = knop.hasAttribute('data-mscvtr-vorige');
        const uit = !kanScrollen || (isVorige ? pos <= 2 : pos >= max - 2);
        knop.disabled = uit;
      });
      const stippen = this.querySelector('[data-mscvtr-stippen]');
      if (stippen && stippen.children.length) {
        const pagina = Math.round(pos / (this.stap || 1));
        [...stippen.children].forEach((s, i) => s.setAttribute('aria-current', i === pagina ? 'true' : 'false'));
      }
    }

    _stippen() {
      const stippen = this.querySelector('[data-mscvtr-stippen]');
      if (!stippen) return;
      const maak = () => {
        const rest = this.spoor.scrollWidth - this.spoor.clientWidth;
        const aantal = rest > 2 ? Math.ceil(rest / (this.stap || 1) - 0.01) + 1 : 1;
        if (stippen.children.length === aantal) return;
        stippen.innerHTML = '';
        // data-mscvtr-stippen="decor": alleen een voortgangsaanduiding (aria-hidden), geen knoppen.
        const decor = stippen.getAttribute('data-mscvtr-stippen') === 'decor';
        if (decor) stippen.setAttribute('aria-hidden', 'true');
        for (let i = 0; i < aantal; i++) {
          const b = document.createElement(decor ? 'span' : 'button');
          b.className = 'mscvtr-carrousel__stip';
          if (!decor) {
            b.type = 'button';
            b.setAttribute('aria-label', `Ga naar ${i + 1} van ${aantal}`);
            b.addEventListener('click', () => this.spoor.scrollTo({ left: i * this.stap, behavior: reduceMotion() ? 'auto' : 'smooth' }));
          }
          stippen.appendChild(b);
        }
        stippen.hidden = aantal < 2;
        this.werkBij();
      };
      maak();
      if ('ResizeObserver' in window) new ResizeObserver(maak).observe(this.spoor);
    }

    _sleep() {
      // Slepen met de muis (aanraken scrolt vanzelf). Een klik na slepen opent de kaart niet.
      let start = null;
      let gesleept = false;
      this.spoor.addEventListener('pointerdown', (e) => {
        if (e.pointerType !== 'mouse' || e.button !== 0) return;
        start = { x: e.clientX, scroll: this.spoor.scrollLeft };
        gesleept = false;
      });
      window.addEventListener('pointermove', (e) => {
        if (!start) return;
        const dx = e.clientX - start.x;
        if (!gesleept && Math.abs(dx) > 5) {
          gesleept = true;
          this.spoor.classList.add('is-slepen');
        }
        if (gesleept) this.spoor.scrollLeft = start.scroll - dx;
      });
      window.addEventListener('pointerup', () => {
        if (!start) return;
        start = null;
        if (gesleept) {
          this.spoor.classList.remove('is-slepen');
          // Laat scroll-snap de dichtstbijzijnde kaart pakken.
          this.spoor.scrollBy({ left: 0 });
        }
      });
      this.spoor.addEventListener('click', (e) => {
        if (gesleept) { e.preventDefault(); e.stopPropagation(); gesleept = false; }
      }, true);
      this.spoor.addEventListener('dragstart', (e) => e.preventDefault());
    }
  }

  let laatstePointer = 0;
  document.addEventListener('pointerdown', () => { laatstePointer = Date.now(); }, true);

  /* ── Lade (zijpaneel) ───────────────────────────────────────────────────────────── */
  /*
   * <mscvtr-lade id="…" [kant="links"]>
   *   <dialog class="mscvtr-lade__dialog" aria-label="…">
   *     <div class="mscvtr-lade__kop"> … <button data-mscvtr-lade-sluit>…</button></div>
   *     <div class="mscvtr-lade__inhoud"> … </div>
   *     <div class="mscvtr-lade__voet"> … </div>          (optioneel)
   *   </dialog>
   * </mscvtr-lade>
   * Openen: elke knop met aria-controls="<id>"; of element.show(). Sluiten: kruisje, Escape, klik naast het paneel.
   * Events op het element: 'mscvtr:lade-open' (vóór openen; ook als 'dialog:before-show' voor oude code),
   * 'mscvtr:lade-dicht' (na sluiten).
   */
  class MscvtrLade extends HTMLElement {
    connectedCallback() {
      if (this._klaar) return;
      this._klaar = true;
      this.dialog = this.querySelector('dialog');
      if (!this.dialog) return;
      this.dialog.addEventListener('click', (e) => {
        if (e.target === this.dialog) this.hide(); // klik op de achtergrond
        if (e.target.closest('[data-mscvtr-lade-sluit]')) this.hide();
      });
      this.dialog.addEventListener('cancel', (e) => { e.preventDefault(); this.hide(); });
    }

    get open() { return !!this.dialog?.open; }

    show(opener) {
      if (!this.dialog || this.open) return Promise.resolve();
      this._opener = opener || document.activeElement;
      this.dispatchEvent(new CustomEvent('dialog:before-show', { bubbles: true }));
      this.dispatchEvent(new CustomEvent('mscvtr:lade-open', { bubbles: true }));
      this.dialog.showModal();
      // Geopend met muis of vinger: focus op het paneel zelf (geen focusrand op het kruisje).
      // Met het toetsenbord blijft de focus op het eerste bedieningselement, zoals het hoort.
      if (Date.now() - laatstePointer < 1000) {
        this.dialog.setAttribute('tabindex', '-1');
        this.dialog.focus({ preventScroll: true });
      }
      document.documentElement.setAttribute('data-mscvtr-lade-open', '');
      this._knoppen(true);
      requestAnimationFrame(() => this.dialog.classList.add('is-open'));
      return Promise.resolve();
    }

    hide() {
      if (!this.dialog || !this.open) return Promise.resolve();
      this.dialog.classList.remove('is-open');
      const sluit = () => {
        this.dialog.close();
        if (!document.querySelector('mscvtr-lade dialog[open]')) document.documentElement.removeAttribute('data-mscvtr-lade-open');
        this._knoppen(false);
        this._opener?.focus?.({ preventScroll: true });
        this.dispatchEvent(new CustomEvent('mscvtr:lade-dicht', { bubbles: true }));
      };
      if (reduceMotion()) { sluit(); return Promise.resolve(); }
      return new Promise((klaar) => setTimeout(() => { sluit(); klaar(); }, 260));
    }

    toggle(opener) { return this.open ? this.hide() : this.show(opener); }

    _knoppen(open) {
      document.querySelectorAll(`[aria-controls="${this.id}"]`).forEach((k) => k.setAttribute('aria-expanded', open ? 'true' : 'false'));
    }
  }

  // Eén klik-luisteraar voor alle open-knoppen (werkt ook voor markup die later wordt ingeladen).
  document.addEventListener('click', (e) => {
    const knop = e.target.closest('[aria-controls]');
    if (!knop) return;
    const doel = document.getElementById(knop.getAttribute('aria-controls'));
    if (doel instanceof MscvtrLade) {
      e.preventDefault();
      doel.toggle(knop);
      return;
    }
    if (doel instanceof MscvtrCarrousel) {
      if (knop.hasAttribute('data-mscvtr-vorige')) doel.vorige();
      if (knop.hasAttribute('data-mscvtr-volgende')) doel.volgende();
    }
  });
  // Knoppen binnen de carrousel zelf (zonder aria-controls).
  document.addEventListener('click', (e) => {
    const knop = e.target.closest('[data-mscvtr-vorige], [data-mscvtr-volgende]');
    if (!knop || knop.hasAttribute('aria-controls')) return;
    const c = knop.closest('mscvtr-carrousel');
    if (!c) return;
    if (knop.hasAttribute('data-mscvtr-vorige')) c.vorige(); else c.volgende();
  });

  if (!customElements.get('mscvtr-tabs')) customElements.define('mscvtr-tabs', MscvtrTabs);
  if (!customElements.get('mscvtr-carrousel')) customElements.define('mscvtr-carrousel', MscvtrCarrousel);
  if (!customElements.get('mscvtr-lade')) customElements.define('mscvtr-lade', MscvtrLade);

  /* ── Vertaallaag Horizon-events → oude namen ────────────────────────────────────── */
  const events = () => import('@shopify/events');

  events().then(({ StandardEvents, CartLinesUpdateEvent }) => {
    MSCVTR.verversWinkelwagen = async ({ open = false } = {}) => {
      const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { headers: { Accept: 'application/json' } });
      const cart = await res.json();
      document.dispatchEvent(new CartLinesUpdateEvent({
        action: open ? 'add' : 'update',
        context: 'cart',
        lines: [],
        promise: Promise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { source: 'mscvtr', itemCount: cart.item_count },
        }),
      }));
      return cart;
    };

    // Variant gekozen in Horizon → 'variant:change' (detail.variant zoals /products/<h>.js het geeft).
    document.addEventListener(StandardEvents.productSelect, (event) => {
      const doel = event.target instanceof Element ? event.target : document;
      Promise.resolve(event.promise).then((uitkomst) => {
        const variant = uitkomst && uitkomst.variant ? uitkomst.variant : null;
        doel.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant, bron: 'horizon' } }));
      }).catch(() => {});
    });

    // Winkelwagen veranderd → 'cart:change' (detail.cart = Shopify's standaard-cart).
    document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
      if (event.detail && event.detail.source === 'mscvtr-vertaal') return;
      Promise.resolve(event.promise).then((uitkomst) => {
        document.dispatchEvent(new CustomEvent('cart:change', { detail: { cart: uitkomst?.cart || null, bron: 'horizon' } }));
      }).catch(() => {});
    });
  }).catch(() => {
    // Zonder Shopify's eventmodule: verversen = pagina-onafhankelijk niets doen, maar nooit een fout.
    MSCVTR.verversWinkelwagen = async () => (await fetch('/cart.js')).json();
  });

  // Oude code vraagt om verversen.
  document.addEventListener('cart:refresh', (e) => {
    const open = !!(e.detail && e.detail.open);
    const ga = () => MSCVTR.verversWinkelwagen && MSCVTR.verversWinkelwagen({ open });
    if (MSCVTR.verversWinkelwagen) ga(); else setTimeout(ga, 300);
  });
})();
